import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { ConfirmDeleteDialog } from "@/components/shared/ConfirmDeleteDialog";
import { TablePagination } from "@/components/shared/TablePagination";
import { Keyword, UtilizationTag } from "@/types";
import { KeywordMasterListAction } from "@/constants";
import { landscapeApi } from "@/resources/landscape";
import { rankTrackerApi } from "@/resources/rank-tracker";
import { keywordApi } from "@/resources/keyword";
import { productApi } from "@/resources/product";
import { useAppData } from "@/contexts/AppDataContext";
import { toast } from "@/hooks/use-toast";
import queryClient from "@/query-client";
import type { HeroKeywordsSortField } from "@/types";
import { RoutePath } from "@/routes";
import type { CreateABTestLocationState } from "@/pages/CreateABTest";
import { KeywordBankTableHeader } from "./KeywordBankTableHeader";
import { KeywordBankSkeletonRow } from "./KeywordBankSkeletonRow";
import { KeywordBankKeywordRow } from "./KeywordBankKeywordRow";
import { useKeywordColumnResize } from "../useResizableColumn";

type SortDirection = "asc" | "desc";

interface KeywordBankTableProps {
  keywords: Keyword[];
  /**
   * Which screen is rendering this table. `landscape` drops Theme, Utilization and Source; every
   * other column, the resizable keyword column, the sorting and the pagination are shared.
   *
   * One table for both screens is what the design reference does -- it renders the same
   * `KeywordTable` for the Keywords page and for a landscape's Keyword Analysis tab. Signal had two
   * implementations instead, and they drifted four separate times before this: the landscape lost
   * the master-list stars, paraphrased the shared column tooltips, misaligned its own header, and
   * grew a second control for an action the star already performed.
   */
  variant?: "bank" | "landscape";
  /**
   * AUT-496. False only where the values cannot be there: the public share payload leaves the
   * SERP Match fields out, so the column would be dashes for every row forever.
   */
  showSerpMatch?: boolean;
  /** This screen's row actions. Omitted, the Keyword Bank's own menu renders. */
  renderRowActions?: (keyword: Keyword) => React.ReactNode;
  heroAsin: string;
  productId: number;
  selectedKeywords: Set<number>;
  onSelectKeyword: (keywordId: number, selected: boolean) => void;
  onUpdateMasterList: (keywordIds: number[], action: KeywordMasterListAction) => void;
  onDeleteKeyword: (keywordId: number) => void;
  onOpenLandscape?: (landscapeId: number) => void;
  isLoading: boolean;
  totalItems: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  sortField: HeroKeywordsSortField;
  sortDirection: SortDirection;
  onSortChange: (field: HeroKeywordsSortField) => void;
  disableSortInteractions?: boolean;
  readOnly?: boolean;
  keywordThemeMap?: Map<number, { themeId: number; themeName: string }>;
  themes?: { id: number; name: string }[];
  onAddKeywordToTheme?: (keywordId: number, themeId: number) => void;
  onRemoveKeywordFromTheme?: (keywordId: number, themeId: number) => void;
  onChangeKeywordTheme?: (keywordId: number, fromThemeId: number, toThemeId: number) => void;
  onOpenAddTheme?: (keywordId?: number) => void;
}

export function KeywordBankTable({
  keywords,
  variant = "bank",
  showSerpMatch = true,
  renderRowActions,
  selectedKeywords,
  onSelectKeyword,
  onUpdateMasterList,
  onDeleteKeyword,
  onOpenLandscape,
  heroAsin,
  productId,
  isLoading,
  totalItems,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  sortField,
  sortDirection,
  onSortChange,
  disableSortInteractions = false,
  readOnly = false,
  keywordThemeMap,
  themes = [],
  onAddKeywordToTheme,
  onRemoveKeywordFromTheme,
  onChangeKeywordTheme,
  onOpenAddTheme,
}: KeywordBankTableProps) {
  const { selectedBrandId } = useAppData();
  const navigate = useNavigate();
  const [keywordToDelete, setKeywordToDelete] = useState<{ id: number; search: string } | null>(null);
  const [keywordToUnassign, setKeywordToUnassign] = useState<{ keywordId: number; themeId: number; search: string; themeName: string } | null>(null);

  const { mutate: createLandscape } = landscapeApi.useCreateLandscape();
  const { mutate: addRankTrackerKeywords } = rankTrackerApi.useAddRankTrackerKeywords();
  const { mutate: deleteRankTrackerKeyword } = rankTrackerApi.useDeleteRankTrackerKeyword();
  const { data: heroAsinData } = productApi.useGetHeroProductABTests(
    String(productId),
    selectedBrandId ?? undefined,
  );

  const onCreateLandscape = (searchPhrase: string) => {
    createLandscape({
      hero_asin: heroAsin,
      search_phrase: searchPhrase,
      brand_id: selectedBrandId,
    });
  };

  const onAddToRankTracker = (keyword: Keyword) => {
    if (!selectedBrandId) return;
    addRankTrackerKeywords(
      {
        brandId: selectedBrandId,
        heroAsin,
        keywords: [keyword.search],
        source: "manual",
        // Target the row that was clicked -- see AddRankTrackerKeywordsParams.
        keywordIds: [keyword.id],
      },
      {
        onSuccess: () => {
          toast({ title: "Added to Rank Tracker", description: `"${keyword.search}" is now tracked.` });
          queryClient.invalidateQueries({ queryKey: keywordApi.getKeywordsQueryPrefix(productId) });
        },
      },
    );
  };

  const onRemoveFromRankTracker = (keyword: Keyword) => {
    if (!selectedBrandId) return;
    deleteRankTrackerKeyword(
      { id: keyword.id, brandId: selectedBrandId },
      {
        onSuccess: () => {
          toast({ title: "Removed from Rank Tracker", description: `"${keyword.search}" is no longer tracked.` });
          queryClient.invalidateQueries({ queryKey: keywordApi.getKeywordsQueryPrefix(productId) });
        },
      },
    );
  };

  const onNavigateToAbTest = (keyword: Keyword) => {
    if (!heroAsinData) return;
    const state: CreateABTestLocationState = {
      heroAsin: heroAsinData,
      productId,
      initialTestType: "keyword",
      initialTargetPhrase: keyword.search,
      prefillFromKeyword: { keyword: keyword.search, source: "keyword" },
      defaultSource: "keyword",
      returnTo: window.location.pathname + window.location.search,
    };
    navigate(RoutePath.CreateABTest, { state });
  };

  const allSelected = keywords.length > 0 && keywords.every((k) => selectedKeywords.has(k.id));

  const handleSelectAll = (checked: boolean) => {
    keywords.map((k) => k.id).forEach((id) => onSelectKeyword(id, checked));
  };

  // Combined width of every OTHER column, so the table's overall min-width
  // grows in lockstep with the sticky column instead of clipping it.
  const OTHER_COLS_WIDTH = readOnly ? 1124 : 1194;

  const { stickyStyle, keywordTextWidth, onResizeStart, onResizeMove, onResizeEnd } =
    useKeywordColumnResize(readOnly);

  const stickyGrid = readOnly
    ? "grid grid-cols-[28px_minmax(0,1fr)] items-center"
    : "grid grid-cols-[40px_28px_minmax(0,1fr)] items-center";

  return (
    <div className="w-full min-w-0 rounded-md border-t border-border border-x-0 border-b-0 bg-card">
      <Table style={{ minWidth: OTHER_COLS_WIDTH + stickyStyle.width }}>
        <KeywordBankTableHeader
          variant={variant}
          showSerpMatch={showSerpMatch}
          readOnly={readOnly}
          stickyStyle={stickyStyle}
          stickyGrid={stickyGrid}
          onResizeStart={onResizeStart}
          onResizeMove={onResizeMove}
          onResizeEnd={onResizeEnd}
          sortField={sortField}
          sortDirection={sortDirection}
          disableSortInteractions={disableSortInteractions}
          allSelected={allSelected}
          onSelectAll={handleSelectAll}
          onSortChange={onSortChange}
        />
        <TableBody>
          {isLoading ? (
            Array.from({ length: Math.max(5, pageSize) })
              .slice(0, 8)
              .map((_, idx) => (
                <KeywordBankSkeletonRow
                  key={`kw-skeleton-row-${idx}`}
                  idx={idx}
                  readOnly={readOnly}
                  stickyStyle={stickyStyle}
                  stickyGrid={stickyGrid}
                />
              ))
          ) : keywords.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={readOnly ? 12 : 13}
                className="p-8 text-center text-muted-foreground text-sm"
              >
                No keywords found.
              </TableCell>
            </TableRow>
          ) : (
            keywords.map((keyword) => (
              <KeywordBankKeywordRow
                variant={variant}
                showSerpMatch={showSerpMatch}
                renderActions={renderRowActions}
                key={keyword.id}
                keyword={keyword}
                isSelected={selectedKeywords.has(keyword.id)}
                readOnly={readOnly}
                stickyStyle={stickyStyle}
                stickyGrid={stickyGrid}
                keywordTextWidth={keywordTextWidth}
                themes={themes}
                keywordThemeMap={keywordThemeMap}
                heroAsinData={heroAsinData}
                onSelectKeyword={onSelectKeyword}
                onUpdateMasterList={onUpdateMasterList}
                onAddKeywordToTheme={onAddKeywordToTheme}
                onChangeKeywordTheme={onChangeKeywordTheme}
                onOpenAddTheme={onOpenAddTheme}
                onOpenLandscape={onOpenLandscape}
                onCreateLandscape={onCreateLandscape}
                onAddToRankTracker={onAddToRankTracker}
                onRemoveFromRankTracker={onRemoveFromRankTracker}
                onNavigateToAbTest={onNavigateToAbTest}
                onRequestDelete={(id, search) => setKeywordToDelete({ id, search })}
                onRequestUnassign={(keywordId, themeId, search, themeName) =>
                  setKeywordToUnassign({ keywordId, themeId, search, themeName })
                }
              />
            ))
          )}
        </TableBody>
      </Table>

      <div className="shrink-0 bg-card">
        <TablePagination
          totalItems={totalItems}
          page={page}
          onPageChange={onPageChange}
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
        />
      </div>

      <ConfirmDeleteDialog
        open={!!keywordToDelete}
        onOpenChange={(open) => { if (!open) setKeywordToDelete(null); }}
        title={`Delete "${keywordToDelete?.search}"?`}
        description="This will permanently remove this keyword from the keyword bank. This action cannot be undone."
        onConfirm={() => {
          if (keywordToDelete) onDeleteKeyword(keywordToDelete.id);
          setKeywordToDelete(null);
        }}
      />

      <ConfirmDeleteDialog
        open={!!keywordToUnassign}
        onOpenChange={(open) => { if (!open) setKeywordToUnassign(null); }}
        title={`Remove "${keywordToUnassign?.search}" from "${keywordToUnassign?.themeName}"?`}
        description="This keyword will become unthemed. It will stay in the keyword bank."
        confirmLabel="Remove"
        onConfirm={() => {
          if (keywordToUnassign) onRemoveKeywordFromTheme?.(keywordToUnassign.keywordId, keywordToUnassign.themeId);
          setKeywordToUnassign(null);
        }}
      />
    </div>
  );
}
